import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { FBLANDING } from "@/constants/testIds";
import { ArrowRight, BadgeCheck, Check, ChevronDown, CircleAlert, ShieldCheck, X } from "lucide-react";

// Color isolation for conversion: #FF3B30 (brand red) is used ONLY on the primary CTA so it stays
// visually unique and the eye is never trained to ignore it as "just brand decoration".
const GOLD = "#F2B807";
const DEFAULT_PRODUCT_NAME = process.env.REACT_APP_CLICKBANK_PRODUCT_NAME;
const DEFAULT_CATEGORY = process.env.REACT_APP_CLICKBANK_PRODUCT_CATEGORY;
const DEFAULT_OFFER_URL = process.env.REACT_APP_CLICKBANK_BRIDGE_URL;

const FAQS = [
  {
    q: "Is this the official product page?",
    a: "No. This is a bridge page that gives you a quick summary before you continue to the vendor's official page to read the full details and decide for yourself.",
  },
  {
    q: "Do you earn money if I buy?",
    a: "Yes — we may earn a commission if you purchase through our link, at no additional cost to you. That's why this disclosure appears clearly on the page.",
  },
  {
    q: "Should I buy right away?",
    a: "Only if the product matches what you need. Read the vendor page carefully, check the pricing and refund policy, and skip it if it doesn't feel like a fit.",
  },
  {
    q: "Who handles payment, delivery, and refunds?",
    a: "The product vendor handles checkout, delivery, customer support, and refunds on the official offer page. Review their terms before purchasing.",
  },
];

function normalizeOfferUrl(raw) {
  if (!raw) return "";
  try {
    const parsed = new URL(raw);
    const isClickBankHost = parsed.hostname === "hop.clickbank.net" || parsed.hostname.endsWith(".clickbank.net") || parsed.hostname.endsWith(".clickbank.com");
    return parsed.protocol === "https:" && isClickBankHost ? parsed.toString() : "";
  } catch {
    return "";
  }
}

function sanitizeBridgeText(raw, fallback, maxLength) {
  if (!raw) return fallback;
  const cleaned = raw.replace(/[<>]/g, "").trim().slice(0, maxLength);
  return cleaned || fallback;
}

function getCTAProps(offerUrl) {
  return {
    href: offerUrl || undefined,
    target: "_blank",
    rel: "nofollow sponsored noreferrer noopener",
    "aria-disabled": !offerUrl,
    tabIndex: offerUrl ? undefined : -1,
  };
}

function CTALink({ offerUrl, children = "Continue to the official page" }) {
  const disabled = !offerUrl;
  return (
    <div data-testid={FBLANDING.form} className="space-y-3">
      <a
        data-testid={FBLANDING.submit}
        {...getCTAProps(offerUrl)}
        className={`w-full inline-flex items-center justify-center gap-2 bg-[#FF3B30] text-white py-4 rounded font-display font-bold text-lg transition-colors shadow-[0_8px_30px_rgba(255,59,48,0.35)] ${disabled ? "opacity-50 pointer-events-none" : "hover:bg-[#E6352B]"}`}
      >
        {children} <ArrowRight className="w-5 h-5" />
      </a>
      <p className="flex items-start gap-2 text-xs text-white/40 pt-1">
        <ShieldCheck className="w-4 h-4 flex-shrink-0 mt-0.5" />
        Affiliate disclosure: we may earn a commission if you buy through this page, at no extra cost to you.
      </p>
    </div>
  );
}

export default function FacebookLanding() {
  const [searchParams] = useSearchParams();
  const [openFaq, setOpenFaq] = useState(0);
  const [showStickyBar, setShowStickyBar] = useState(false);
  const heroRef = useRef(null);
  const config = useMemo(() => {
    const rawOfferUrl = DEFAULT_OFFER_URL || "";
    const offerUrl = normalizeOfferUrl(rawOfferUrl);
    return {
      productName: sanitizeBridgeText(searchParams.get("product") || DEFAULT_PRODUCT_NAME, "this offer", 80),
      category: sanitizeBridgeText(searchParams.get("category") || DEFAULT_CATEGORY, "digital product", 50),
      offerUrl,
      invalidOfferUrl: Boolean(rawOfferUrl) && !offerUrl,
    };
  }, [searchParams]);
  const offerConfigMessage = config.invalidOfferUrl
    ? "The configured offer link must be a valid HTTPS ClickBank URL before this bridge can send visitors onward."
    : process.env.NODE_ENV !== "production"
      ? "Add REACT_APP_CLICKBANK_BRIDGE_URL to activate the button."
      : "The official offer link is not active yet. Please check back soon.";

  useEffect(() => {
    document.title = `Before you buy ${config.productName} — read this first`;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute("content", `A simple bridge page to help visitors decide whether ${config.productName} looks like a fit before continuing to the official offer page.`);
  }, [config.productName]);

  useEffect(() => {
    const onScroll = () => {
      const heroBottom = heroRef.current?.getBoundingClientRect().bottom ?? 0;
      setShowStickyBar(heroBottom < 0);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <header className="border-b border-white/10 relative z-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
          <img src="/logo.png" alt="The Managerly" className="h-7 w-auto" />
          <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-white/45">
            Bridge page
          </div>
        </div>
      </header>

      <section
        ref={heroRef}
        data-testid={FBLANDING.hero}
        className="relative overflow-hidden"
        style={{
          backgroundImage:
            "linear-gradient(180deg, rgba(10,10,10,0.55) 0%, rgba(10,10,10,0.92) 75%, #0A0A0A 100%), url(https://images.pexels.com/photos/7567443/pexels-photo-7567443.jpeg)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14 md:py-20 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <p className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.25em] mb-6 px-3 py-1.5 rounded-full border" style={{ color: GOLD, borderColor: "rgba(242,184,7,0.35)", backgroundColor: "rgba(242,184,7,0.08)" }}>
              <BadgeCheck className="w-3 h-3" /> Quick summary · Official link next
            </p>
            <h1 className="font-display font-extrabold text-4xl sm:text-5xl md:text-6xl tracking-tighter leading-[0.95] mb-6">
              Before you buy <span className="italic font-light text-[#FF3B30]">{config.productName}</span>,<br />
              read this first.
            </h1>
            <p className="text-lg text-white/70 max-w-xl mb-8">
              This page is here to help you decide whether this {config.category} is worth your time.
              In one minute, you'll know what to check, what to avoid, and whether to continue to the official offer page.
            </p>

            <div className="flex flex-wrap gap-x-6 gap-y-2 mb-8">
              {["Clear disclosure", "No hype", "Official vendor page linked below"].map((t) => (
                <span key={t} className="inline-flex items-center gap-1.5 text-sm text-white/80">
                  <Check className="w-4 h-4" style={{ color: GOLD }} /> {t}
                </span>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-x-8 gap-y-3 text-sm font-mono" style={{ color: "rgba(255,255,255,0.5)" }}>
              <span>Your first ClickBank bridge</span>
              <span className="hidden sm:inline">·</span>
              <span>One focused next step</span>
              <span className="hidden sm:inline">·</span>
              <span className="inline-flex items-center gap-1" style={{ color: GOLD }}>
                <ShieldCheck className="w-3.5 h-3.5" /> Read before you click
              </span>
            </div>
          </div>

          <div className="md:col-span-5">
            <div className="rounded-lg p-6 sm:p-7" style={{ backgroundColor: "rgba(20,20,20,0.9)", border: "1px solid rgba(255,255,255,0.1)", backdropFilter: "blur(6px)" }}>
              <h2 className="font-display text-2xl mb-1">Quick fit check</h2>
              <p className="text-white/50 text-sm mb-5">Use this page to decide whether you should continue to the product vendor.</p>
              {!config.offerUrl && (
                <div className="mb-4 flex items-start gap-3 rounded-lg border border-[#FF3B30]/30 bg-[#FF3B30]/10 px-4 py-3 text-sm text-white/80">
                  <CircleAlert className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#FF3B30]" />
                  <p>{offerConfigMessage}</p>
                </div>
              )}
              <CTALink offerUrl={config.offerUrl} />
              <ul className="mt-5 space-y-3 text-sm text-white/65">
                <li className="flex gap-2"><Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: GOLD }} /> Read the official page with more context</li>
                <li className="flex gap-2"><Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: GOLD }} /> Check price, refund terms, and delivery details</li>
                <li className="flex gap-2"><Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: GOLD }} /> Skip it if the offer doesn't match what you need</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-14 border-t border-white/10">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
          {[
            { n: "01", t: "Know what this page is", d: "A bridge page is not the checkout page. It's a short stop before the vendor page." },
            { n: "02", t: "Use it to pre-qualify yourself", d: "If the offer sounds relevant after this summary, continue. If not, leave without buying." },
            { n: "03", t: "Let the vendor close the sale", d: "The official offer page should handle the final details, payment, and refund terms." },
          ].map((s) => (
            <div key={s.n}>
              <div className="font-mono text-2xl mb-2" style={{ color: GOLD }}>{s.n}</div>
              <div className="font-display text-xl mb-2">{s.t}</div>
              <p className="text-white/60 text-sm leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-14 border-t border-white/10">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] mb-4" style={{ color: GOLD }}>Good fit if</p>
            <ul className="space-y-3 text-white/70">
              {[
                "You already know the problem you want solved.",
                "You plan to read the vendor page before buying.",
                "You are looking for one product, not magic promises.",
              ].map((item) => (
                <li key={item} className="flex gap-2"><Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: GOLD }} /> {item}</li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] mb-4" style={{ color: GOLD }}>Probably skip if</p>
            <ul className="space-y-3 text-white/70">
              {[
                "You expect guaranteed results of any kind.",
                "You have not checked the full pricing or refund details.",
                "You feel rushed or unsure about what the product actually does.",
              ].map((item) => (
                <li key={item} className="flex gap-2"><X className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#FF3B30]" /> {item}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-14 border-t border-white/10">
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] mb-8 text-center" style={{ color: GOLD }}>Questions</p>
        <div className="space-y-2">
          {FAQS.map((f, i) => {
            const open = openFaq === i;
            return (
              <div key={f.q} className="rounded border border-white/10 overflow-hidden">
                <button
                  onClick={() => setOpenFaq(open ? -1 : i)}
                  className="w-full flex items-center justify-between gap-4 px-4 py-4 text-left hover:bg-white/[0.03] transition-colors"
                >
                  <span className="font-display text-lg">{f.q}</span>
                  <ChevronDown className={`w-4 h-4 flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`} style={{ color: GOLD }} />
                </button>
                {open && (
                  <p className="px-4 pb-4 text-white/60 text-sm leading-relaxed">{f.a}</p>
                )}
              </div>
            );
          })}
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-16 border-t border-white/10 text-center">
        <h2 className="font-display text-3xl sm:text-4xl tracking-tight mb-4">If it looks relevant, continue.</h2>
        <p className="text-white/60 mb-8 max-w-md mx-auto">
          You're not buying on this page. You're just choosing whether to continue to the official vendor page.
        </p>
        <div className="max-w-md mx-auto">
          <CTALink offerUrl={config.offerUrl}>Continue to the official vendor page</CTALink>
        </div>
      </section>

      <footer className="border-t border-white/10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-white/40">
          <span>© {new Date().getFullYear()} The Managerly · London</span>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <Link to="/privacy" className="hover:text-[#FF3B30] transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-[#FF3B30] transition-colors">Terms</Link>
            <a href="mailto:hello@the-managerly.com" className="hover:text-[#FF3B30] transition-colors">Contact</a>
          </div>
        </div>
      </footer>

      {showStickyBar && config.offerUrl && (
        <div className="fixed bottom-0 inset-x-0 z-30 sm:hidden p-3" style={{ backgroundColor: "rgba(10,10,10,0.97)", borderTop: "1px solid rgba(255,255,255,0.1)", backdropFilter: "blur(8px)" }}>
          <a {...getCTAProps(config.offerUrl)} data-testid={FBLANDING.stickySubmit}
            className="w-full inline-flex items-center justify-center gap-2 bg-[#FF3B30] text-white py-3.5 rounded font-display font-bold hover:bg-[#E6352B] transition-colors">
            Continue to offer <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      )}
    </div>
  );
}
